import type { NavGroup } from '@titan/shared';
import { Bot, Boxes, Calculator, ChevronDown, LayoutDashboard, Menu, Route, Settings2, Ticket, type LucideIcon } from 'lucide-react';
import { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router';
import styles from './Shell.module.css';

/** Icons by nav group key. A module whose group has no icon yet gets the generic one. */
const ICONS: Record<string, LucideIcon> = {
  ops: LayoutDashboard,
  mod: Route,
  roi: Calculator,
  ai: Bot,
  tick: Ticket,
  admin: Settings2,
};
const iconFor = (key: string): LucideIcon => ICONS[key] ?? Boxes;

const cx = (...parts: Array<string | false | undefined | null>) => parts.filter(Boolean).join(' ');

function groupIsActive(group: NavGroup, pathname: string): boolean {
  return group.items.some((i) => pathname === i.path || pathname.startsWith(`${i.path}/`)) || pathname === group.path;
}

/**
 * Spec 00. Clicking a group navigates to its first screen and toggles its sub-list;
 * clicking it while already inside only toggles. Collapsed shows icons only.
 */
export function Sidebar({ nav }: { nav: NavGroup[] }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [open, setOpen] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(nav.map((g) => [g.key, groupIsActive(g, pathname)])),
  );

  const onGroup = (group: NavGroup) => {
    const inside = groupIsActive(group, pathname);
    if (group.items.length <= 1) {
      navigate(group.path);
      return;
    }
    if (!inside) navigate(group.path);
    setOpen((o) => ({ ...o, [group.key]: inside ? !o[group.key] : true }));
  };

  return (
    <nav className={cx(styles.sidebar, collapsed && styles.collapsed)} aria-label="Modules">
      <button type="button" className={styles.menuButton} onClick={() => setCollapsed((c) => !c)} aria-expanded={!collapsed}>
        <Menu size={19} strokeWidth={1.75} aria-hidden="true" />
        {!collapsed && <span>Menu</span>}
      </button>
      {nav.map((group) => {
        const Icon = iconFor(group.key);
        const active = groupIsActive(group, pathname);
        const showSub = !collapsed && group.items.length > 1 && (open[group.key] ?? false);
        return (
          <div key={group.key}>
            <button
              type="button"
              className={cx(styles.group, active && styles.active)}
              onClick={() => onGroup(group)}
              aria-current={active ? 'page' : undefined}
              title={collapsed ? group.label : undefined}
            >
              <Icon size={19} strokeWidth={1.75} aria-hidden="true" />
              {!collapsed && <span className={styles.groupLabel}>{group.label}</span>}
              {!collapsed && group.items.length > 1 && (
                <ChevronDown
                  size={15}
                  strokeWidth={1.75}
                  aria-hidden="true"
                  className={cx(styles.caret, !(open[group.key] ?? false) && styles.caretClosed)}
                />
              )}
            </button>
            {showSub && (
              <div className={styles.subList}>
                {group.items.map((item) => (
                  <NavLink
                    key={item.key}
                    to={item.path}
                    end={item.path === group.path && group.items.some((i) => i !== item && i.path.startsWith(group.path))}
                    className={({ isActive }) => cx(styles.subItem, isActive && styles.active)}
                  >
                    <span className={styles.subDot} />
                    {item.label}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
}
